import { FormEvent, useCallback, useEffect, useState } from "react"
import { useOutletContext } from "react-router-dom"
import { Shield, Mail, KeyRound, Smartphone, ExternalLink } from "lucide-react"
import { UserDetails } from "../../../../types"
import { useAppDispatch, useToast } from "../../../hooks"
import { getMe } from "../../../../redux/slices/auth"
import { PrimaryBtn, DefaultBtn } from "../../buttons"
import BBDialog from "../../dialog"
import api from "../../../../common/utils/axiosApi"

interface OutletContext {
  user: UserDetails
}

interface MfaEnrollment {
  id: string
  status: string
  enrolled_at: string
  last_auth: string
  type: string
  auth_method: string
}

const AccountSecurity = () => {
  const { user } = useOutletContext<OutletContext>()
  const dispatch = useAppDispatch()
  const { pushSuccessToast, pushErrorToast } = useToast()

  // Password reset state
  const [resetLoading, setResetLoading] = useState(false)
  const [resetDisabled, setResetDisabled] = useState(false)

  // Email change state
  const [newEmail, setNewEmail] = useState("")
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailError, setEmailError] = useState("")

  // MFA state
  const [mfaLoading, setMfaLoading] = useState(true)
  const [mfaActionLoading, setMfaActionLoading] = useState(false)
  const [mfaEnrollments, setMfaEnrollments] = useState<MfaEnrollment[]>([])
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)

  // Fetch MFA status on mount
  const fetchMfaStatus = useCallback(async () => {
    try {
      const { data } = await api.post("/api/v1/auth0-mfa-status")
      if (data.status === "success") {
        setMfaEnrollments(data.enrollments ?? [])
      }
    } catch {
      // silently fail — MFA section will show as not enabled
    } finally {
      setMfaLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMfaStatus()
  }, [fetchMfaStatus])

  // Password reset handler
  const handlePasswordReset = async () => {
    setResetLoading(true)
    try {
      const { data } = await api.post("/api/v1/auth0-password-reset")
      if (data.status === "success" && data.ticket) {
        window.open(data.ticket, "_blank")
        setResetDisabled(true)
        setTimeout(() => setResetDisabled(false), 10000)
      } else if (data.status === "success") {
        pushSuccessToast("Check your email for a password reset link.")
        setResetDisabled(true)
        setTimeout(() => setResetDisabled(false), 10000)
      } else {
        pushErrorToast(data.message || "Failed to send password reset email.")
      }
    } catch {
      pushErrorToast("Failed to send password reset email.")
    } finally {
      setResetLoading(false)
    }
  }

  // Email change handler
  const handleEmailChange = async (e: FormEvent) => {
    e.preventDefault()
    setEmailError("")

    const trimmed = newEmail.trim()
    if (!trimmed) {
      setEmailError("Email is required.")
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError("Please enter a valid email address.")
      return
    }

    setEmailLoading(true)
    try {
      const { data } = await api.post("/api/v1/auth0-email-change", {
        new_email: trimmed
      })
      if (data.status === "success") {
        pushSuccessToast(
          "Email updated. A verification email has been sent to your new address."
        )
        setNewEmail("")
        dispatch(getMe())
      } else {
        setEmailError(data.message || "Failed to update email.")
      }
    } catch {
      setEmailError("Failed to update email.")
    } finally {
      setEmailLoading(false)
    }
  }

  // MFA enable handler
  const handleEnableMfa = async () => {
    setMfaActionLoading(true)
    try {
      const { data } = await api.post("/api/v1/auth0-mfa-enable")
      if (data.status === "success" && data.data?.enroll_url) {
        window.location.href = data.data.enroll_url
      } else {
        pushErrorToast(data.message || "Failed to enable MFA.")
        setMfaActionLoading(false)
      }
    } catch {
      pushErrorToast("Failed to enable MFA.")
      setMfaActionLoading(false)
    }
  }

  // MFA disable handler
  const handleDisableMfa = async () => {
    setShowDisableConfirm(false)
    setMfaActionLoading(true)
    try {
      const { data } = await api.post("/api/v1/auth0-mfa-disable")
      if (data.status === "success") {
        pushSuccessToast("MFA has been disabled.")
        setMfaEnrollments([])
      } else {
        pushErrorToast(data.message || "Failed to disable MFA.")
      }
    } catch {
      pushErrorToast("Failed to disable MFA.")
    } finally {
      setMfaActionLoading(false)
    }
  }

  const hasMfa = mfaEnrollments.length > 0

  return (
    <div className="px-6">
      <h2 className="font-bold text-xl py-6 flex items-center gap-2">
        <Shield size={22} />
        Account Security
      </h2>

      <div className="divide-y divide-neutral-200">
        {/* Email Section */}
        <div className="py-6">
          <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide flex items-center gap-2 mb-4">
            <Mail size={16} />
            Email
          </h3>
          <p className="text-sm text-neutral-500 mb-1">Current email</p>
          <p className="text-sm font-medium text-neutral-900 mb-4">
            {user.email}
          </p>
          <form onSubmit={handleEmailChange} className="flex items-start gap-3">
            <div className="flex-1 max-w-sm">
              <input
                type="email"
                className={`w-full border ${
                  emailError ? "border-red-400" : "border-neutral-200"
                } px-3 py-2 rounded-md text-sm`}
                placeholder="New email address"
                value={newEmail}
                onChange={e => {
                  setNewEmail(e.target.value)
                  setEmailError("")
                }}
              />
              {emailError && (
                <p className="text-red-500 text-xs mt-1">{emailError}</p>
              )}
            </div>
            <PrimaryBtn
              type="submit"
              disabled={emailLoading || !newEmail.trim()}
              isLoading={emailLoading}
              text="sm"
              size="sm"
            >
              Update Email
            </PrimaryBtn>
          </form>
        </div>

        {/* Password Section */}
        <div className="py-6">
          <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide flex items-center gap-2 mb-4">
            <KeyRound size={16} />
            Password
          </h3>
          <PrimaryBtn
            onClick={handlePasswordReset}
            disabled={resetLoading || resetDisabled}
            isLoading={resetLoading}
            text="sm"
            size="sm"
          >
            Reset Password
            <ExternalLink size={14} className="ml-1.5" />
          </PrimaryBtn>
          <p className="text-sm text-neutral-400 mt-2">
            Close the new tab after you are finished.
          </p>
        </div>

        {/* MFA Section */}
        <div className="py-6">
          <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide flex items-center gap-2 mb-4">
            <Smartphone size={16} />
            Two-Factor Authentication
          </h3>
          {mfaLoading ? (
            <p className="text-sm text-neutral-400">Loading MFA status...</p>
          ) : hasMfa ? (
            <>
              <div className="mb-4">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
                  Enabled
                </span>
              </div>
              {mfaEnrollments.map(enrollment => (
                <div
                  key={enrollment.id}
                  className="text-sm text-neutral-600 mb-1"
                >
                  {enrollment.auth_method || enrollment.type} — enrolled{" "}
                  {new Date(enrollment.enrolled_at).toLocaleDateString()}
                  {enrollment.last_auth && (
                    <span className="text-neutral-400">
                      {" "}
                      · last used{" "}
                      {new Date(enrollment.last_auth).toLocaleDateString()}
                    </span>
                  )}
                </div>
              ))}
              <div className="mt-4">
                <DefaultBtn
                  onClick={() => setShowDisableConfirm(true)}
                  disabled={mfaActionLoading}
                  isLoading={mfaActionLoading}
                  text="sm"
                  size="sm"
                >
                  Disable MFA
                </DefaultBtn>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 bg-neutral-100 px-2.5 py-1 rounded-full">
                  Not enabled
                </span>
              </div>
              <p className="text-sm text-neutral-500 mb-4">
                Add an extra layer of security by requiring a code from your
                authenticator app when you sign in.
              </p>
              <PrimaryBtn
                onClick={handleEnableMfa}
                disabled={mfaActionLoading}
                isLoading={mfaActionLoading}
                text="sm"
                size="sm"
              >
                Enable MFA
              </PrimaryBtn>
            </>
          )}
        </div>
      </div>

      <BBDialog
        open={showDisableConfirm}
        title="Disable MFA"
        body="Are you sure you want to disable multi-factor authentication? Your account will be less secure."
        okLabel="Disable MFA"
        cancelLabel="Cancel"
        onOk={handleDisableMfa}
        onCancel={() => setShowDisableConfirm(false)}
        onClose={() => setShowDisableConfirm(false)}
      />
    </div>
  )
}

export default AccountSecurity
